import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CLAIM_CALL_ASSISTANCE_REASON_OFF_ROAD,
  CLAIM_CALL_ASSISTANCE_REASON_VEHICLE_NOT_SAFE_OTHER_SUBTYPE,
  type ClaimCallAssistanceLocationState,
} from '../claims/claimCallAssistanceLocationState'
import { buildClaimOtherSubtypeHref } from '../claims/claimFlowModel'
import { CLUB4X4_CONTACT_US_HREF } from '../claims/contactLinks'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard, PhoneIcon, PrimaryButton } from './ui'

const CLAIMS_PHONE_DISPLAY = '1300 296 296'
const CLAIMS_PHONE_TEL = 'tel:1300296296'

/**
 * Shown when self-serve cannot continue: vehicle not safe/legal to drive (answered No on the
 * PreClaim safe-to-drive gate), the user chose the `off-road` other-claim subtype (direct to this
 * screen), or similar phone-triage paths. Same phone and contact page for all.
 */
export default function ClaimCallAssistanceStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const navState = location.state as ClaimCallAssistanceLocationState | null
  const isOffRoad = navState?.assistanceReason === CLAIM_CALL_ASSISTANCE_REASON_OFF_ROAD
  const backTargetsOtherSubtype =
    isOffRoad ||
    navState?.assistanceReason === CLAIM_CALL_ASSISTANCE_REASON_VEHICLE_NOT_SAFE_OTHER_SUBTYPE

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleBack = () => {
    if (backTargetsOtherSubtype) {
      const ref = navState?.policyRef ?? navState?.policy?.policyRef ?? null
      const hint = navState?.typeHint ?? null
      navigate(buildClaimOtherSubtypeHref(ref, hint), {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
      return
    }
    navigate('/claims')
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel={backTargetsOtherSubtype ? 'Back' : 'Back to claims'}
        onBack={handleBack}
        eyebrow="Claim"
        title="Please call us"
      />

      <ClaimsFlowMain>
        <FormCard>
          <div className="flex flex-col gap-[20px] items-start w-full">
            <h2 className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900 m-0">
              We cannot continue this claim online
            </h2>
            <div className="font-body font-regular text-md leading-md text-text-secondary-700 w-full flex flex-col gap-[12px]">
              {isOffRoad ? (
                <>
                  <p className="m-0">
                  So that our team can arrange the right help and next steps, we need to speak to you to lodge an off-road recovery claim. Please call us when you can.
                  </p>
                </>
              ) : (
                <>
                  <p className="m-0">
                    If your vehicle isn&apos;t safe or legal to drive, we need to speak to you so our team can arrange the right help and next steps. Please call us when you can.
                  </p>
                </>
              )}
            </div>

            <div className="flex flex-col gap-[8px] items-start w-full pt-[4px]">
              <p className="m-0 font-body font-semibold text-md text-text-primary-900">
                Call Club 4X4 Insurance
              </p>
              <a
                href={CLAIMS_PHONE_TEL}
                className="inline-flex items-center gap-[10px] font-display font-semibold text-display-xs md:text-[28px] leading-none text-foreground-brand-secondary-alt underline decoration-2 underline-offset-4"
              >
                <PhoneIcon className="size-7 shrink-0 text-foreground-brand-secondary-alt" aria-hidden />
                {CLAIMS_PHONE_DISPLAY}
              </a>
            </div>

            <p className="m-0 font-body font-regular text-md leading-md text-text-secondary-700 w-full">
              <a
                href={CLUB4X4_CONTACT_US_HREF}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                Open contact page
                <span className="sr-only"> (opens in new tab)</span>
              </a>
            </p>

            <div className="flex flex-col gap-[12px] w-full pt-[8px]">
              <PrimaryButton fullWidth type="button" onClick={() => navigate('/dashboard')}>
                Back to dashboard
              </PrimaryButton>
            </div>
          </div>
        </FormCard>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
